import { ChangeEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
//
import {
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
} from '@mui/material';
// components
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import {
  ProviderCityPriceDialog,
  ProviderCityPricesTableRow,
} from '../components';
// api
import { getSettingsData } from '@/features/app/api';
import { getProviderCityPrices } from '../api';
import { getVariants } from '@/features/services-management/api';
// hooks + utils
import useSettings from '@/hooks/useSettings';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useToggle from '@/hooks/useToggle';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  applySortFilterProviderCityPrices,
  getServiceVariantName,
  isServiceVariantActive,
  PROVIDER_CITY_PRICE_TABLE_HEAD,
} from '../utils';
// routes
import { PATH_USER } from '@/features/app';
import { PATH_SETTINGS } from '../routes/paths';
// types
import { ProviderCityPriceFilters } from '../types';

export default function ProviderCityPricesList() {
  const { themeStretch } = useSettings();
  const allow = useGetAccess('settings');
  const { errorMsg } = useSnackbarMsg();
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrderBy: 'end_date',
    defaultOrder: 'asc',
    rowsPerPageStorageKey: 'settings-provider-city-prices-rows-per-page',
  });

  const {
    toggle: isPriceDialogOpen,
    onOpen: openPriceDialog,
    onClose: closePriceDialog,
  } = useToggle();

  const [searchText, setSearchText] = useState('');
  const [filterCityId, setFilterCityId] = useState('all');
  const [filterVariantId, setFilterVariantId] = useState('all');
  const [filterActiveOn, setFilterActiveOn] = useState('');

  const providerCityPriceFilters = useMemo<ProviderCityPriceFilters>(
    () => ({
      city_id: filterCityId !== 'all' ? filterCityId : undefined,
      service_variant_id:
        filterVariantId !== 'all' ? filterVariantId : undefined,
      active_on: filterActiveOn || undefined,
    }),
    [filterActiveOn, filterCityId, filterVariantId]
  );

  const { data: settingsData, isLoading: isSettingsDataLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) =>
      errorMsg(error, `Something went wrong while fetching settings data`),
    refetchOnWindowFocus: false,
  });

  const { data: serviceVariants, isLoading: isServiceVariantsLoading } =
    useQuery({
      queryKey: ['serviceVariants'],
      queryFn: getVariants,
      onError: (error) =>
        errorMsg(error, `Something went wrong while fetching service variants`),
      refetchOnWindowFocus: false,
    });

  const { data: providerCityPrices, isLoading: isPricesLoading } = useQuery({
    queryKey: ['providerCityPrices', providerCityPriceFilters],
    queryFn: () => getProviderCityPrices(providerCityPriceFilters),
    onError: (error) =>
      errorMsg(error, `Something went wrong while fetching provider prices`),
  });

  const activeServiceVariants = useMemo(
    () => (serviceVariants || []).filter(isServiceVariantActive),
    [serviceVariants]
  );

  const serviceVariantOptions = useMemo(
    () =>
      activeServiceVariants.map((variant) => ({
        id: variant.id,
        label: `${variant.service.name} / ${getServiceVariantName(variant)}`,
      })),
    [activeServiceVariants]
  );

  const providerCityPricesWithVariantNames = useMemo(
    () =>
      (providerCityPrices || []).flatMap((price) => {
        const variant = activeServiceVariants.find(
          (item) => item.id === price.service_variant_id
        );

        if (!variant) return [];

        return [
          {
            ...price,
            service_variant: {
              ...price.service_variant,
              name: variant.name,
              service: variant.service,
            },
          },
        ];
      }),
    [activeServiceVariants, providerCityPrices]
  );

  const dataFiltered = applySortFilterProviderCityPrices({
    tableData: providerCityPricesWithVariantNames,
    comparator: getComparator(order, orderBy),
    searchText,
    activeOn: filterActiveOn,
  });

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    setPage(0);
  };

  const handleFilterCityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterCityId(event.target.value);
    setPage(0);
  };

  const handleFilterVariantChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterVariantId(event.target.value);
    setPage(0);
  };

  const handleFilterActiveOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterActiveOn(event.target.value);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchText('');
    setFilterCityId('all');
    setFilterVariantId('all');
    setFilterActiveOn('');
    setPage(0);
  };

  const denseHeight = dense ? 52 : 72;
  const isNotFound = !dataFiltered.length;
  const showSkeleton = isPricesLoading || isServiceVariantsLoading;

  return (
    <Page title="Settings: Provider Prices">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Provider Prices"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Settings', href: PATH_SETTINGS.root },
            { name: 'Provider Prices' },
          ]}
          action={
            allow.canCreate && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={openPriceDialog}
              >
                Add provider price
              </Button>
            )
          }
        />

        <Card>
          <Stack
            spacing={2}
            direction={{ xs: 'column', md: 'row' }}
            sx={{
              py: 2.5,
              px: 3,
              alignItems: { md: 'center' },
              flexWrap: 'wrap',
            }}
          >
            <TextField
              value={searchText}
              onChange={(event) => handleSearchTextChange(event.target.value)}
              placeholder="Search provider prices..."
              size="small"
              sx={{ minWidth: { md: 260 }, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:search-fill"
                      sx={{ color: 'text.disabled', width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              label="City"
              value={filterCityId}
              onChange={handleFilterCityChange}
              size="small"
              sx={{ minWidth: { md: 180 } }}
              disabled={isSettingsDataLoading}
            >
              <MenuItem value="all">All cities</MenuItem>
              {(settingsData?.cities || []).map((city) => (
                <MenuItem key={city.id} value={String(city.id)}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Service variant"
              value={filterVariantId}
              onChange={handleFilterVariantChange}
              size="small"
              sx={{ minWidth: { md: 240 } }}
              disabled={isServiceVariantsLoading}
            >
              <MenuItem value="all">All variants</MenuItem>
              {serviceVariantOptions.map((variant) => (
                <MenuItem key={variant.id} value={String(variant.id)}>
                  {variant.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Active on"
              type="date"
              value={filterActiveOn}
              onChange={handleFilterActiveOnChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { md: 160 } }}
            />

            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Iconify icon="eva:refresh-outline" />}
              onClick={handleResetFilters}
              sx={{ minWidth: { md: 110 } }}
            >
              Reset
            </Button>
          </Stack>

          {showSkeleton ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1100, position: 'relative' }}>
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={PROVIDER_CITY_PRICE_TABLE_HEAD}
                      rowCount={dataFiltered.length}
                      onSort={onSort}
                    />
                    <TableBody>
                      {dataFiltered
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((price) => (
                          <ProviderCityPricesTableRow
                            key={price.id}
                            row={price}
                            canUpdate={allow.canUpdate}
                            canDelete={allow.canDelete}
                          />
                        ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          page,
                          rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No provider prices available"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>

        <ProviderCityPriceDialog
          isOpen={isPriceDialogOpen}
          onClose={closePriceDialog}
        />
      </Container>
    </Page>
  );
}
